<script setup>
import SaveSubTaskAction from '@frontJs/components/actions/projectManagement/tasks/SaveSubTaskAction.vue';
import SaveTaskAction from '@frontJs/components/actions/projectManagement/tasks/SaveTaskAction.vue';
import SaveTaskTimeAction from '@frontJs/components/actions/projectManagement/tasks/SaveTaskTimeAction.vue';
import ToggleTaskStatusAction from '@frontJs/components/actions/projectManagement/tasks/ToggleTaskStatusAction.vue';
import TaskOnHoldCell from '@frontJs/components/tables/cells/projectManagement/tasks/TaskOnHoldCell.vue';
import CaretDownIcon from '@iconify-vue/ph/caret-down';
import CheckIcon from '@iconify-vue/ph/check';
import ClockIcon from '@iconify-vue/ph/clock';
import FileTextIcon from '@iconify-vue/ph/file-text';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import XIcon from '@iconify-vue/ph/x';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatDate, formatTime } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { toastCreated, toastSaved } from '@js/utils/toast';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const {
    userSessionKey = null,
    type = null,
    status = null,
    task = null,
    theme = null,
    noOneResponsible = null,
    createTaskUrl,
} = defineProps({
    userSessionKey: {
        type: String,
        required: false,
    },
    type: {
        type: [String, Array],
        required: false,
    },
    status: {
        type: [String, Array],
        required: false,
    },
    task: {
        type: Object,
        required: false,
    },
    theme: {
        type: [String, Array],
        required: false,
    },
    noOneResponsible: {
        type: Boolean,
        required: false,
    },
    createTaskUrl: {
        type: String,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'id',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ID',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.id,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (cellProps) =>
                h(TextCell, {
                    text: cellProps.row.original.fullName,
                    length: 160,
                    lineThrough:
                        (status === 'open' ||
                            status === 'to_process' ||
                            (status && status.includes('open')) ||
                            (status && status.includes('to_process'))) &&
                        cellProps.row.parentId &&
                        (cellProps.row.original.status === 'closed' ||
                            cellProps.row.original.status === 'processed'),
                    minWidth: 400,
                }),
        }),
        columnHelper.display({
            id: 'details',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Détails',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            props.row.original.description
                                ? h(
                                      DisplayContentAction,
                                      {
                                          title: 'Description',
                                          content:
                                              props.row.original.description,
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  square: true,
                                                  title: 'Voir la description',
                                                  type: 'primary',
                                                  icon: FileTextIcon,
                                              }),
                                      },
                                  )
                                : h('div', { class: 'size-8' }, ' '),
                            props.row.original.internalComment
                                ? h(
                                      DisplayContentAction,
                                      {
                                          title: 'Commentaire interne',
                                          content:
                                              props.row.original
                                                  .internalComment,
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  square: true,
                                                  title: 'Voir le commentaire interne',
                                                  type: 'secondary',
                                                  icon: FileTextIcon,
                                              }),
                                      },
                                  )
                                : h('div', { class: 'size-8' }, ' '),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'taskManager',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Responsable tâche',
                }),
            cell: (cellProps) =>
                h(TextCell, {
                    text: cellProps.row.original.users
                        .map((user) => user.fullName)
                        .join(', '),
                    lineThrough:
                        (status === 'open' ||
                            status === 'to_process' ||
                            (status && status.includes('open')) ||
                            (status && status.includes('to_process'))) &&
                        cellProps.row.parentId &&
                        (cellProps.row.original.status === 'closed' ||
                            cellProps.row.original.status === 'processed'),
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'status',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Statut',
                }),
            cell: (props) =>
                props.row.original.status === 'open' ||
                props.row.original.status === 'to_process'
                    ? h(TextCell, {
                          text:
                              props.row.original.status === 'open'
                                  ? 'Ouvert'
                                  : props.row.original.status === 'to_process'
                                    ? 'À traiter'
                                    : 'Non définie',
                      })
                    : h(PillCell, {
                          pillValue:
                              props.row.original.status === 'closed'
                                  ? 'Fermé'
                                  : props.row.original.status === 'processed'
                                    ? 'Traité'
                                    : 'Non définie',
                          type:
                              props.row.original.status === 'closed'
                                  ? 'success'
                                  : props.row.original.status === 'processed'
                                    ? 'success'
                                    : 'secondary',
                      }),
        }),
        columnHelper.accessor(() => null, {
            id: 'priority',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Priorité',
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue:
                        props.row.original.priority === 'low'
                            ? 'Basse'
                            : props.row.original.priority === 'medium'
                              ? 'Moyenne'
                              : props.row.original.priority === 'high'
                                ? 'Haute'
                                : 'Non définie',
                    type:
                        props.row.original.priority === 'low'
                            ? 'success'
                            : props.row.original.priority === 'medium'
                              ? 'warning'
                              : props.row.original.priority === 'high'
                                ? 'danger'
                                : 'primary',
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'time',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Temps facturé',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: formatTime(
                        props.row.original.hours,
                        props.row.original.minutes,
                    ),
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'deadline',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Date limite',
                }),
            cell: (cellProps) =>
                h(TextCell, {
                    text: formatDate(cellProps.row.original.deadline),
                    color:
                        new Date(cellProps.row.original.deadline) < new Date()
                            ? 'red'
                            : null,
                    lineThrough:
                        (status === 'open' ||
                            status === 'to_process' ||
                            (status && status.includes('open')) ||
                            (status && status.includes('to_process'))) &&
                        cellProps.row.parentId &&
                        (cellProps.row.original.status === 'closed' ||
                            cellProps.row.original.status === 'processed'),
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'onHold',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Attente<br/>client',
                }),
            cell: (props) =>
                h(TaskOnHoldCell, {
                    task: props.row.original,
                    callback: updateData,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'updatedAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'MÀJ le',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.updatedAt,
                }),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            props.row.original.status === 'open' ||
                            props.row.original.status === 'to_process'
                                ? h(
                                      ToggleTaskStatusAction,
                                      {
                                          task: props.row.original,
                                          action:
                                              props.row.original.status ===
                                              'to_process'
                                                  ? 'process'
                                                  : 'close',
                                          callback: () => {
                                              toastSaved();
                                              updateData();
                                          },
                                      },
                                      {
                                          button: ({ action, loading }) =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  type: 'secondary',
                                                  square: true,
                                                  callback: () => action(),
                                                  title:
                                                      props.row.original
                                                          .status ===
                                                      'to_process'
                                                          ? 'Marquer comme en cours'
                                                          : 'Marquer comme terminée',
                                                  icon:
                                                      props.row.original
                                                          .status ===
                                                      'to_process'
                                                          ? CheckIcon
                                                          : XIcon,
                                                  loading,
                                              }),
                                      },
                                  )
                                : null,
                            props.row.original.status === 'open' ||
                            props.row.original.status === 'to_process'
                                ? h(
                                      SaveTaskTimeAction,
                                      {
                                          task: props.row.original,
                                          callback: () => {
                                              toastSaved();
                                              updateData();
                                          },
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  type: 'secondary',
                                                  square: true,
                                                  title: 'Ajouter du temps',
                                                  icon: ClockIcon,
                                              }),
                                      },
                                  )
                                : null,
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Modifier la tâche',
                                icon: PencilSimpleIcon,
                                href: route(
                                    'front.projectManagement.task.updateTask',
                                    {
                                        task: props.row.original.id,
                                    },
                                ),
                            }),
                            !props.row.original.isChild
                                ? h(
                                      SaveTaskAction,
                                      {
                                          task: props.row.original,
                                          callback: () => {
                                              toastSaved();
                                              updateData();
                                          },
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  type: 'secondary',
                                                  square: true,
                                                  title: 'Modifier la tâche',
                                                  icon: PencilSimpleIcon,
                                              }),
                                      },
                                  )
                                : null,
                            props.row.original.status === 'open' ||
                            props.row.original.status === 'to_process'
                                ? h(
                                      SaveSubTaskAction,
                                      {
                                          task: !props.row.original.isChild
                                              ? props.row.original
                                              : props.row.original.parent,
                                          subTask: props.row.original.isChild
                                              ? props.row.original
                                              : null,
                                          callback: () => {
                                              toastSaved();
                                              updateData();
                                          },
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  type: 'secondary',
                                                  square: true,
                                                  title: !props.row.original
                                                      .isChild
                                                      ? 'Ajouter une sous-tâche'
                                                      : 'Modifier la sous-tâche',
                                                  icon: !props.row.original
                                                      .isChild
                                                      ? PlusIcon
                                                      : PencilSimpleIcon,
                                              }),
                                      },
                                  )
                                : null,
                            props.row.getCanExpand()
                                ? h(ActionButton, {
                                      size: 'small',
                                      type: 'secondary',
                                      square: true,
                                      title: props.row.getIsExpanded()
                                          ? 'Réduire les sous-tâches'
                                          : 'Étendre les sous-tâches',
                                      callback: () =>
                                          props.row.toggleExpanded(),
                                      icon: CaretDownIcon,
                                      rotateIcon: props.row.getIsExpanded(),
                                  })
                                : null,
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.task.postTasks'),
    data: {
        type,
        status,
        taskId: task ? task.id : null,
        theme,
        noOneResponsible,
    },
    userSessionKey,
    columnPinning: {
        right: ['actions'],
    },
    columnVisibility: {
        updatedAt: status === 'closed',
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #actions>
            <SaveSubTaskAction
                v-if="task"
                :task="task"
                :callback="
                    () => {
                        toastCreated();
                        updateData();
                    }
                "
            >
                <template #trigger>
                    <ActionButton
                        size="small"
                        title="Ajouter une sous-tâche"
                        :left-icon="PlusIcon"
                    >
                        Ajouter
                    </ActionButton>
                </template>
            </SaveSubTaskAction>

            <ActionButton
                v-else
                size="small"
                :href="createTaskUrl"
                :left-icon="PlusIcon"
                title="Ajouter une tâche"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
